// Columna derecha de la vista Reglas: la ficha de la regla elegida. La suma de los pesos se muestra
// siempre porque el lambda rechaza cualquier regla cuyos pesos no den 100.
import type { ReactNode } from 'react';
import { Scale } from 'lucide-react';
import { Badge, Button } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    areWeightsValid,
    describeActor,
    describeStrategy,
    effectiveThreshold,
    weightsSum,
    NO_THRESHOLD_LABEL,
    WEIGHTS_TOTAL,
} from '../logic';
import type { RuleRow } from '../types';

const FIELD_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

/** El wire de Dev devuelve `priorityWeights: {}`: no traer los pesos NO es incumplir la suma. */
export const NO_WEIGHTS_NOTE = 'Esta regla no trae los pesos del ranking.';

export const WEIGHT_LABEL = {
    storeMatch: 'Coincide la tienda',
    loadAvailable: 'Carga disponible',
    seniority: 'Veteranía',
} as const;

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <div className={FIELD_LABEL}>{label}</div>
            <div className="mt-0.5 text-[12px]">{children}</div>
        </div>
    );
}

export interface RuleDetailProps {
    /** `null` pinta el estado vacío de la columna. */
    rule: RuleRow | null;
    canUpdateRule: boolean;
    onActivate: (rule: RuleRow) => void;
    onEdit: (rule: RuleRow) => void;
}

export function RuleDetail({ rule, canUpdateRule, onActivate, onEdit }: RuleDetailProps) {
    if (!rule) {
        return (
            <MasterDetailEmpty
                icon={<Scale className="size-10" aria-hidden />}
                title="Selecciona una regla"
                sub="Elige una regla de la lista para ver con qué criterios reparte la cartera."
            />
        );
    }

    const sum = weightsSum(rule.priorityWeights);
    const threshold = effectiveThreshold(rule.maxLoadPerCollector);

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="border-b border-border px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-[15px] font-bold leading-tight">
                        {describeStrategy(rule.strategy)}
                    </h2>
                    <Badge variant={rule.active ? 'success' : 'secondary'}>
                        {rule.active ? 'Activa' : 'Inactiva'}
                    </Badge>
                </div>
            </div>

            <div className="space-y-3 px-4 py-3">
                <Field label="Tope por cobrador">{threshold ?? NO_THRESHOLD_LABEL}</Field>

                <div>
                    <div className={FIELD_LABEL}>Pesos del ranking</div>
                    {sum === null ? (
                        <p className="mt-1 text-[12px] text-muted-foreground">{NO_WEIGHTS_NOTE}</p>
                    ) : (
                        <>
                            <ul className="mt-1 space-y-0.5 text-[12px] tabular-nums">
                                <li>
                                    {WEIGHT_LABEL.storeMatch}: {rule.priorityWeights.storeMatch}
                                </li>
                                <li>
                                    {WEIGHT_LABEL.loadAvailable}:{' '}
                                    {rule.priorityWeights.loadAvailable}
                                </li>
                                <li>
                                    {WEIGHT_LABEL.seniority}: {rule.priorityWeights.seniority}
                                </li>
                            </ul>
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                Suma: <strong className="font-bold">{sum}</strong> de{' '}
                                {WEIGHTS_TOTAL}
                                {areWeightsValid(rule.priorityWeights)
                                    ? ''
                                    : ' · esta regla no cumple la suma que el motor exige'}
                            </p>
                        </>
                    )}
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-3">
                    <Field label="Creada por">{describeActor(rule.createdBy)}</Field>
                    <Field label="Actualizada por">{describeActor(rule.updatedBy)}</Field>
                    <Field label="Creada el">{formatDateTimeMx(rule.createdAt)}</Field>
                    <Field label="Actualizada el">{formatDateTimeMx(rule.updatedAt)}</Field>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
                {canUpdateRule && !rule.active ? (
                    <Button
                        size="sm"
                        onClick={() => {
                            onActivate(rule);
                        }}
                    >
                        Activar
                    </Button>
                ) : null}
                {canUpdateRule ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            onEdit(rule);
                        }}
                    >
                        Editar
                    </Button>
                ) : (
                    <span className="text-[11px] text-muted-foreground">
                        No tienes permiso para modificar las reglas de asignación.
                    </span>
                )}
            </div>
        </div>
    );
}
